import { useEffect, useLayoutEffect, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import type { ObservationRow } from "../types";
import { FitLine } from "./FitLine";
import { Sparkline } from "./Sparkline";
import { cardsThatFit, changeNote, intoRows, MAX_CARDS_PER_ROW, rowSizes } from "./vitalsCards";

const Root = styled(Paper)(({ theme }) => ({ padding: theme.spacing(2, 2.5) }));

const Heading = styled(Typography)(({ theme }) => ({
  ...theme.typography.h2,
  marginBottom: theme.spacing(1.5),
}));

/** The rows of cards, one under the other. */
const Rows = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

/** One row of cards: its columns are set from how many cards it holds, so they share the panel's width
 *  evenly (at most three a row, never an empty column). */
const Grid = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1.5),
}));

const Label = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  display: "block",
  marginBottom: theme.spacing(0.75),
}));

/** One observation per card, in three rows (name, reading, trend) that are the grid's own rows
 *  (subgrid): a row of cards keeps its readings and trends aligned, and a name that wraps to two lines
 *  makes only that row taller, with no blank line reserved under every one-line name. */
const Tile = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
  display: "grid",
  gridRow: "span 3",
  gridTemplateRows: "subgrid",
  rowGap: theme.spacing(1),
  minWidth: 0,
}));

const Metric = styled(Typography)(({ theme }) => ({
  ...theme.typography.overline,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  lineHeight: 1.5,
  overflowWrap: "anywhere",
}));

/** A font size that shrinks with its ``FitLine`` (``--fit`` is 1 unless the line is too long for its card). */
const fit = (rem: number) => `calc(${rem}rem * var(--fit, 1))`;

const Reading = styled("span")(() => ({
  fontSize: fit(1.25),
  fontWeight: 600,
  lineHeight: 1.15,
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
}));

/** "(↑ from 120 mmHg)" beside the reading. */
const Note = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  fontSize: fit(0.75),
  fontWeight: 400,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(0.75),
}));

const Unit = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  fontSize: fit(0.75),
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(0.5),
}));

/** The blood pressure card's mean, at the right of its reading. */
const Mean = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  fontSize: fit(0.6875),
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
  marginLeft: "auto",
  paddingLeft: theme.spacing(0.5),
  "& > span": { fontSize: "inherit", marginLeft: theme.spacing(0.375) },
}));

const Trend = styled(Box)(() => ({
  alignSelf: "end",
}));

const Empty = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

/** Systolic and diastolic, shown as one "158/94" tile whenever both are there, with the mean (MAP) of the
 *  same measurement at the right of the reading. */
const PRESSURE_PAIRS: { systolic: string; diastolic: string; mean: string; label: string }[] = [
  {
    systolic: "systolic_blood_pressure",
    diastolic: "diastolic_blood_pressure",
    mean: "mean_arterial_pressure",
    label: "blood pressure",
  },
  { systolic: "ibp1_systolic", diastolic: "ibp1_diastolic", mean: "ibp1_mean", label: "ibp1 pressure" },
];

type Card =
  | { kind: "single"; row: ObservationRow }
  | { kind: "pressure"; label: string; systolic: ObservationRow; diastolic: ObservationRow; mean?: ObservationRow };

/** The latest readings as tiles, a pressure pair (and its mean) merged into the systolic's place. Merged
 *  only when they are from one measurement (same time): pairing a fresh systolic with a stale diastolic would show a
 *  reading that was never taken. */
function cards(latest: ObservationRow[]): Card[] {
  const byMetric = new Map(latest.map((row) => [row.metric, row]));
  const merged = new Map<string, Card>();
  const absorbed = new Set<string>();
  for (const pair of PRESSURE_PAIRS) {
    const systolic = byMetric.get(pair.systolic);
    const diastolic = byMetric.get(pair.diastolic);
    if (systolic && diastolic && systolic.observed_at === diastolic.observed_at) {
      const candidate = byMetric.get(pair.mean);
      const mean = candidate && candidate.observed_at === systolic.observed_at ? candidate : undefined;
      merged.set(pair.systolic, { kind: "pressure", label: pair.label, systolic, diastolic, mean });
      absorbed.add(pair.diastolic);
      if (mean) absorbed.add(pair.mean);
    }
  }
  return latest
    .filter((row) => !absorbed.has(row.metric))
    .map((row) => merged.get(row.metric) ?? { kind: "single", row });
}

/** "45 s", "1 min", "1 min 15 s", "2 h 5 min". */
function duration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return s % 60 ? `${minutes} min ${s % 60} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
}

/** "just now", "1 min ago", "3 h ago", "2 days ago". */
function ago(seconds: number): string {
  if (seconds < 30) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${Math.max(1, minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** The device's own trend interval, from the readings: the typical gap between one vital's samples. */
function deviceInterval(observations: ObservationRow[]): number | null {
  const byMetric = new Map<string, number[]>();
  for (const row of observations) {
    const times = byMetric.get(row.metric) ?? [];
    times.push(new Date(row.observed_at).getTime());
    byMetric.set(row.metric, times);
  }
  const gaps: number[] = [];
  byMetric.forEach((times) => {
    times.sort((a, b) => a - b);
    for (let i = 1; i < times.length; i += 1) if (times[i] > times[i - 1]) gaps.push((times[i] - times[i - 1]) / 1000);
  });
  if (gaps.length === 0) return null;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
}

/** The page re-reads the mission within this (its poll; a socket message makes it sooner). */
const PAGE_REFRESH_S = 10;

/** Why these readings can differ from the live monitor beside this page, in one line: how often they update
 *  (the slower of the device's trend interval and the engine's read interval) and so how old a reading can be
 *  (the device's interval, plus the engine's, plus the page's refresh). */
function lagText(deviceEvery: number | null, readEvery: number | undefined): string {
  const device = deviceEvery ?? 60;
  const read = readEvery ?? 15;
  const behind = Math.round((device + read + PAGE_REFRESH_S) / 5) * 5;
  return `Updates about every ${duration(Math.max(device, read))}, so readings can be up to ~${duration(behind)} behind the live monitor.`;
}

const cardKey = (card: Card): string => (card.kind === "pressure" ? card.systolic.metric : card.row.metric);

function renderCard(card: Card, observations: ObservationRow[]) {
  const seriesOf = (metric: string) =>
    observations.filter((observation) => observation.metric === metric).map((observation) => observation.value);
  if (card.kind === "pressure") {
    const { systolic, diastolic, mean } = card;
    return (
      <Tile key={cardKey(card)}>
        <Metric>{card.label}</Metric>
        <FitLine sx={{ display: "flex", alignItems: "baseline" }}>
          <Reading>
            {systolic.value}/{diastolic.value}
            <Unit>{systolic.unit}</Unit>
          </Reading>
          {mean ? (
            <Mean title={`Mean pressure ${mean.value} ${mean.unit}`}>
              MAP {mean.value}
              <Unit>{mean.unit}</Unit>
            </Mean>
          ) : null}
        </FitLine>
        <Trend>
          <Sparkline
            values={seriesOf(systolic.metric)}
            others={[seriesOf(diastolic.metric)]}
            labels={[
              `SYS ${systolic.value} (${changeNote(systolic, false)})`,
              `DIA ${diastolic.value} (${changeNote(diastolic, false)})`,
            ]}
            height={40}
          />
        </Trend>
      </Tile>
    );
  }
  const { row } = card;
  const note = changeNote(row, true);
  return (
    <Tile key={cardKey(card)}>
      <Metric>{row.metric.replace(/_/g, " ")}</Metric>
      <FitLine>
        <Reading>
          {row.value}
          <Unit>{row.unit}</Unit>
        </Reading>
        <Note>({note})</Note>
      </FitLine>
      <Trend>
        <Sparkline values={seriesOf(row.metric)} />
      </Trend>
    </Tile>
  );
}

type VitalsPanelProps = {
  latest: ObservationRow[];
  observations: ObservationRow[];
  /** How often the engine reads the device's new trend rows (seconds; the dashboard's). */
  trendReadIntervalS?: number;
  title?: string;
  emptyText?: string;
  /** Inside another panel (the mission header): no card of its own. */
  embedded?: boolean;
};

export function VitalsPanel(props: VitalsPanelProps) {
  const newest = props.latest.reduce<string | null>(
    (latest, row) => (latest === null || row.observed_at > latest ? row.observed_at : latest),
    null,
  );
  // Ticks, so "1 min ago" keeps counting between refreshes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);
  const newestAt = newest ? new Date(newest) : null;
  const sameDay = newestAt && newestAt.toDateString() === new Date(now).toDateString();
  const updated = newestAt
    ? newestAt.toLocaleString(undefined, {
        ...(sameDay ? {} : { month: "short", day: "numeric" }),
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : null;
  const age = newestAt ? ago((now - newestAt.getTime()) / 1000) : null;
  // How wide the cards' panel is, to know how many fit side by side.
  const theme = useTheme();
  const gap = Number.parseFloat(theme.spacing(1.5));
  const [holder, setHolder] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!holder) return undefined;
    setWidth(holder.clientWidth);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(holder);
    return () => observer.disconnect();
  }, [holder]);
  const title = `${props.title ?? "Latest observations"}${updated ? ` · as of ${updated} · ${age}` : ""}`;
  const Frame = props.embedded ? Box : Root;
  const Title = props.embedded ? Label : Heading;
  const heading = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Title sx={{ marginBottom: 0 }}>{title}</Title>
      <Tooltip
        title={lagText(deviceInterval(props.observations), props.trendReadIntervalS)}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        componentsProps={{ tooltip: { sx: { maxWidth: "none" } } }}
      >
        <IconButton size="small" aria-label="Why these can differ from the live monitor" sx={{ p: 0.25 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
  const shown = cards(props.latest);
  if (props.latest.length === 0) {
    return (
      <Frame>
        <Box sx={{ mb: props.embedded ? 0.75 : 1.5 }}>{heading}</Box>
        <Empty>{props.emptyText ?? "No observations recorded yet."}</Empty>
      </Frame>
    );
  }
  return (
    <Frame>
      <Box sx={{ mb: props.embedded ? 0.75 : 1.5 }}>{heading}</Box>
      <Rows ref={setHolder}>
        {intoRows(shown, rowSizes(shown.length, width === null ? MAX_CARDS_PER_ROW : cardsThatFit(width, gap))).map(
          (row) => (
            <Grid key={row.map(cardKey).join()} sx={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
              {row.map((card) => renderCard(card, props.observations))}
            </Grid>
          ),
        )}
      </Rows>
    </Frame>
  );
}
